import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  FaLock, FaUser, FaTrash, FaExclamationTriangle,
  FaArrowLeft, FaSignOutAlt, FaShieldAlt
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005';

function AccountSettings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [finalConfirm, setFinalConfirm] = useState(false);

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteError('');

    if (!deletePassword) {
      setDeleteError('Please enter your password to confirm deletion.');
      return;
    }

    if (!finalConfirm) {
      setFinalConfirm(true);
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/auth/account`, {
        data: { password: deletePassword }
      });
      logout();
      navigate('/', { state: { accountDeleted: true } });
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to delete account. Please try again.';
      setDeleteError(message);
      setFinalConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeletePassword('');
    setDeleteError('');
    setFinalConfirm(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FaLock className="text-[#2186EB] text-2xl" />
            <h1 className="text-2xl font-bold text-gray-900">MirageDocs</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/dashboard"
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
            >
              <FaArrowLeft /> Dashboard
            </Link>
            <button
              onClick={() => { logout(); navigate('/'); }}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
            >
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-2">
          <FaUser className="text-[#2186EB]" />
          Account Settings
        </h2>

        {/* Account Info */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FaShieldAlt className="text-[#2186EB]" />
            Account Information
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Email</span>
              <span className="font-medium text-gray-900">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Plan</span>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                {user?.plan?.toUpperCase() || 'FREE'}
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-gray-600">Member since</span>
              <span className="font-medium text-gray-900">
                {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white rounded-lg shadow border-2 border-red-200 p-6">
          <h3 className="text-lg font-semibold text-red-700 mb-2 flex items-center gap-2">
            <FaExclamationTriangle />
            Danger Zone
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            Once you delete your account, there is no going back. This action is permanent.
          </p>

          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
            >
              <FaTrash /> Delete My Account
            </button>
          ) : (
            <form onSubmit={handleDeleteAccount} className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-800 mb-2">This will permanently delete:</h4>
                <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                  <li>Your account and login credentials</li>
                  <li>All your uploaded files and encrypted data</li>
                  <li>All your intake portals and their submissions</li>
                  {user?.plan !== 'free' && (
                    <li>Your active subscription (will be canceled immediately)</li>
                  )}
                </ul>
              </div>

              <div>
                <label htmlFor="deletePassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Enter your password to confirm
                </label>
                <input
                  id="deletePassword"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="w-full rounded-md border border-gray-300 p-3 focus:ring-2 focus:ring-red-400 focus:border-red-400"
                  placeholder="Your current password"
                  autoComplete="current-password"
                  disabled={deleting}
                />
              </div>

              {deleteError && (
                <div className="text-red-600 text-sm font-medium bg-red-50 border border-red-200 rounded p-3">
                  {deleteError}
                </div>
              )}

              {finalConfirm && (
                <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 text-yellow-800 font-medium text-sm">
                  <FaExclamationTriangle className="inline mr-2" />
                  Are you absolutely sure? This cannot be undone. Click "Permanently Delete" again to confirm.
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={deleting}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
                    finalConfirm
                      ? 'bg-red-800 text-white hover:bg-red-900'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  } ${deleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <FaTrash />
                  {deleting ? 'Deleting...' : finalConfirm ? 'Permanently Delete' : 'Delete My Account'}
                </button>
                <button
                  type="button"
                  onClick={cancelDelete}
                  disabled={deleting}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default AccountSettings;
